/**
 * Resolves whether a piece of content is BBC-syndicated.
 *
 * INTERIM IMPLEMENTATION.
 * The content payload returned by the generic slug endpoint carries no
 * publisher/partner field, so once a user is on /content/play/video/:slug there
 * is nothing on the object identifying it as BBC. The only authority today is
 * membership of the /api/bbc-urdu listing, so we page through it once and cache
 * the ids.
 *
 * This matters because BBC's spec requires that content from other publishers
 * never triggers BBC events — a false positive is a compliance breach, so this
 * module fails CLOSED: if the allowlist cannot be fetched, nothing is BBC.
 *
 * REPLACE WHEN the backend adds a flag (e.g. `partner: 'bbc'`) to the content
 * record. Only the body of isBbcContent() changes; callers stay as they are.
 */

import { API } from './api.config';

// Safety cap so a malformed totalPage can never spin the loop.
const MAX_PAGES = 20;

let allowlist = null; // Set<number> once resolved
let inFlight = null; // de-dupes concurrent priming calls

async function fetchAllPages() {
  const ids = new Set();
  let page = 1;
  let totalPage = 1;

  while (page <= totalPage && page <= MAX_PAGES) {
    const { data } = await API.get(`api/bbc-urdu?page=${page}`);
    const items = (data && data.items) || [];

    items.forEach((item) => {
      const id = item && item.content && item.content.id;
      if (id !== undefined && id !== null) ids.add(Number(id));
    });

    totalPage = Number(data && data.totalPage) || 1;
    page += 1;
  }

  return ids;
}

/**
 * Warm the cache. Safe to call repeatedly; concurrent callers share one request.
 * @returns {Promise<Set<number>|null>} null when the lookup failed.
 */
export function primeBbcAllowlist() {
  if (allowlist) return Promise.resolve(allowlist);

  if (!inFlight) {
    inFlight = fetchAllPages().then(
      (ids) => {
        allowlist = ids;
        inFlight = null;
        return ids;
      },
      (error) => {
        // Leave the cache empty so the next caller retries.
        console.error('BBC allowlist lookup failed; BBC tracking disabled', error);
        inFlight = null;
        return null;
      }
    );
  }

  return inFlight;
}

/**
 * @param {number|string} contentId
 * @returns {Promise<boolean>} false whenever provenance cannot be confirmed.
 */
export async function isBbcContent(contentId) {
  if (contentId === undefined || contentId === null) return false;

  const ids = allowlist || (await primeBbcAllowlist());
  return ids ? ids.has(Number(contentId)) : false;
}
